import React from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import type { DriverEconomicsData, TierDistribution, DriverEconDriver } from './use-finance-driver-economics.js';

function fmt(n: number | null | undefined): string {
  if (n == null) return '\u2014';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

const TIER_COLOR: Record<string, string> = {
  ELITE: C.gold, STRONG: C.green, AVERAGE: C.text, BELOW_AVG: '#FBBF24', CRITICAL: C.red,
};

const TIER_LABEL: Record<string, string> = {
  ELITE: 'Elite (120+)', STRONG: 'Strong (80-119)', AVERAGE: 'Average (60-79)', BELOW_AVG: 'Below Avg (40-59)', CRITICAL: 'Critical (<40)',
};

function DriverRow({ d, rank }: { d: DriverEconDriver; rank?: number }) {
  const color = TIER_COLOR[d.tier] || C.textMuted;
  return (
    <div style={{ padding: '8px 12px', borderRadius: 8, background: C.panel, border: `1px solid ${C.borderSoft}`, display: 'grid', gridTemplateColumns: rank != null ? '24px 1fr auto' : '1fr auto', gap: 10, alignItems: 'center' }}>
      {rank != null && <div style={{ fontSize: 12, color: C.textMuted, textAlign: 'center' }}>{rank}</div>}
      <div>
        <div style={{ fontSize: 13, color: C.text }}>{d.name}</div>
        <div style={{ fontSize: 11, color: C.textMuted }}>{d.avg_stops_day} stops/day \u00b7 {d.days_worked}d \u00b7 {d.total_stops} total</div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999, color, background: `${color}20` }}>{d.tier}</span>
        {d.total_pay != null && <div style={{ fontSize: 12, color: C.gold, marginTop: 4 }}>{fmt(d.total_pay)}</div>}
      </div>
    </div>
  );
}

interface Props { data: DriverEconomicsData; onRefresh: () => void; }

export default function FinanceDriverEconomics({ data, onRefresh }: Props) {
  const { tier_distribution, top_performers, bottom_performers, company_avg_stops, company_avg_pay_per_stop, total_drivers, period } = data;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{total_drivers} drivers</div>
          <div style={{ fontSize: 12, color: C.textMuted }}>
            Avg {company_avg_stops} stops/day \u00b7 ${company_avg_pay_per_stop.toFixed(2)}/stop \u00b7 {period.weeks_included} week{period.weeks_included !== 1 ? 's' : ''}
          </div>
        </div>
        <button onClick={onRefresh} style={{ background: 'none', border: `1px solid ${C.borderSoft}`, borderRadius: 6, color: C.textMuted, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>Refresh</button>
      </div>

      {/* Tier distribution */}
      {tier_distribution.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold, marginBottom: 10 }}>Performance tiers</div>
          <div style={{ display: 'grid', gap: 8 }}>
            {tier_distribution.map((t: TierDistribution) => {
              const color = TIER_COLOR[t.tier] || C.textMuted;
              const pct = Math.round(t.pct * 100);
              return (
                <div key={t.tier} style={{ display: 'grid', gridTemplateColumns: '160px 1fr auto', gap: 12, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color }}>{TIER_LABEL[t.tier] || t.tier}</span>
                  <div style={{ height: 8, background: C.borderSoft, borderRadius: 4 }}>
                    <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 4 }} />
                  </div>
                  <span style={{ fontSize: 12, color: C.textMuted, minWidth: 60, textAlign: 'right' }}>{t.count} ({pct}%)</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top performers */}
      {top_performers.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.green, marginBottom: 10 }}>Top performers</div>
          <div style={{ display: 'grid', gap: 6 }}>
            {top_performers.map((d, i) => <DriverRow key={d.driver_id || i} d={d} rank={i + 1} />)}
          </div>
        </div>
      )}

      {/* Bottom performers */}
      {bottom_performers.length > 0 && (
        <div style={LAYOUT.subPanel}>
          <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.red, marginBottom: 10 }}>Needs attention</div>
          <div style={{ display: 'grid', gap: 6 }}>
            {bottom_performers.map((d, i) => <DriverRow key={d.driver_id || i} d={d} />)}
          </div>
        </div>
      )}
    </div>
  );
}
